// Berechtigungsmodell V2 (Welle 0) — reine, I/O-freie Domänenlogik (wie rbac.ts).
//
// Zwei Ebenen, strikt getrennt (docs/BERECHTIGUNGSSYSTEM.md Abschnitt 2):
//   • Position → Rang (RANK aus rbac.ts) — WIE HOCH jemand sitzt.
//   • Rechte (Rights) → WAS jemand einzeln darf: Bereichs-Rechte "bereich:lesen"/"bereich:bearbeiten"
//     (Bearbeiten schließt Lesen ein) + heiße Zusatzrechte (löschen/storno/dsgvo/freigabe/umsatz).
//
// KERN-INVARIANTE (M1 / Regelwerk R6): der Mindestrang (RIGHT_MIN_RANK) ist eine KONTINUIERLICHE Hürde
// PRO RECHT — kein Paket. Ein Recht wirkt nur, wenn der Rang seinen Floor erreicht:
//     effectiveRights(user) = angehakte Rechte ∩ { r : userRank(user) >= floor(r) }
// Ein 90er-Recht zu halten berechtigt NICHT zu allen 90er-Rechten — jedes Recht wird einzeln vergeben,
// der Floor begrenzt nur, WER es tragen darf. hasRight() ist die maßgebliche (rang-bewusste) Prüfung.

import { isSuperadmin, userRank, type UserLike } from './rbac'

/** Bereiche (Reiter) mit Zugriffsstufe Aus/Lesen/Bearbeiten. */
export const AREAS = [
  'annahme',
  'werkstatt',
  'planung',
  'kunden',
  'arbeitszeit',
  'inventar',
  'einkauf',
  'rechnungen',
  'leistungen',
  'team',
  'werkstatt_einstellungen',
] as const
export type Area = (typeof AREAS)[number]

/** Alle vergebbaren Rechte: Bereichs-Rechte (bereich:stufe), Sicht-Scopes und heiße Zusatzrechte.
 *
 * HINWEIS zu :lesen-Tokens (Audit 13.07.): Die 22 Bereichs-Rechte entstehen SYMMETRISCH aus AREAS × {lesen,
 * bearbeiten}. Einige :lesen-Tokens steuern bewusst nur die Frontend-Navigation und gaten serverseitig KEINE
 * eigene Route — das ist kein Loch, sondern Design:
 *   • `team:lesen` — reserviertes UI-/Struktur-Token; `GET /users` bleibt breit lesbar, weil die Auftrags-
 *     Zuweisung die Mechaniker-Liste braucht (ein Gate hier bräche die Zuweisung). Team-VERWALTUNG ist über
 *     `team:bearbeiten` (Rang 90) geschützt.
 *   • `annahme:lesen` / `werkstatt:lesen` — Nav-Sichtbarkeit; die AUFTRAGS-Sicht selbst ist über die Scopes
 *     `alle_auftraege` (alles) vs. Fallback (nur eigene) gescopt, nicht über diese Tokens.
 *   • `meine_auftraege` — Sicht-Scope-Marker (nicht „tot"): Fallback-Semantik „nur eigene Aufträge".
 * `leistungen:lesen` gatet dagegen serverseitig `GET /services` mit (breites OR inkl. Auftragsersteller). */
export const RIGHTS = [
  // Bereiche — je Lesen/Bearbeiten
  'annahme:lesen', 'annahme:bearbeiten',
  'werkstatt:lesen', 'werkstatt:bearbeiten',
  'planung:lesen', 'planung:bearbeiten',
  'kunden:lesen', 'kunden:bearbeiten',
  'arbeitszeit:lesen', 'arbeitszeit:bearbeiten',
  'inventar:lesen', 'inventar:bearbeiten',
  'einkauf:lesen', 'einkauf:bearbeiten',
  'rechnungen:lesen', 'rechnungen:bearbeiten',
  'leistungen:lesen', 'leistungen:bearbeiten',
  'team:lesen', 'team:bearbeiten',
  'werkstatt_einstellungen:lesen', 'werkstatt_einstellungen:bearbeiten',
  'produktivitaet:lesen',
  // Belegungs-Plantafel (W10, 15.07.): eigenständiges Rechte-Paar NEBEN dem Bereich 'planung' (Dienstplan) —
  // Plantafel-Zugriff öffnet KEINE Soll-Zeiten (und umgekehrt). BEWUSST (noch) kein AREAS-Eintrag: die
  // Bereichs-Matrix im Frontend ist Record<Area,…>-typisiert und gehört der Frontend-Lane; ein AREAS-Eintrag
  // bräche deren Typecheck cross-lane. Guards laufen right-basiert über canReadBelegung/canWriteBelegung
  // (Muster produktivitaet:lesen). Promotion zum Vollbereich später zusammen mit den Matrix-Labels möglich.
  'belegung:lesen', 'belegung:bearbeiten',
  // Audit-Log-Viewer (16.07.): Lesesicht auf die append-only Nachweiskette (audit_log) der EIGENEN Werkstatt —
  // DSGVO Art. 5(2)/GoBD. BEWUSST kein ':bearbeiten'-Pendant: die Kette ist per DB-Trigger unveränderlich,
  // es EXISTIERT kein Schreibpfad. Wie 'belegung' (noch) kein AREAS-Eintrag (Frontend-Matrix-Typisierung);
  // Guard läuft right-basiert über canReadAudit. Betreiber-Globalsicht hängt NICHT hieran (requireSuperadmin).
  'audit:lesen',
  // Sicht-Scopes (reine Leserechte)
  'alle_auftraege',
  'meine_auftraege',
  // Heiße Zusatzrechte
  'auftrag_loeschen',
  'rechnung_storno',
  'kunden_dsgvo',
  'zeitkorrektur_freigabe',
  'umsatz',
] as const
export type Right = (typeof RIGHTS)[number]

/**
 * Mindestrang je Recht (CAP_MIN_RANK, L1 / docs Abschnitt 2.6). Nur mächtige Rechte tragen einen Floor;
 * alles Übrige = 0 (keine Rang-Schranke). Werte so gewählt, dass alle ROLE_PRESETS_V2 gültig bleiben.
 */
export const RIGHT_MIN_RANK: Partial<Record<Right, number>> = {
  // 90+ (Zweitchef/Chef)
  'team:bearbeiten': 90,
  'werkstatt_einstellungen:bearbeiten': 90,
  'kunden_dsgvo': 90,
  'rechnung_storno': 90,
  'zeitkorrektur_freigabe': 90,
  'arbeitszeit:bearbeiten': 90, // fremde Stempel korrigieren — Entscheidung 10.07. (Parität zum Alt-isBoss)
  'umsatz': 90,
  'audit:lesen': 90, // Nachweiskette (Mitarbeiter-/Kunden-Bezug) — nur Werkstatt-Leitung (Entscheidung 16.07.)
  // 70+ (Werkstattmeister)
  'auftrag_loeschen': 70,
  // 45+ (Verkauf)
  'rechnungen:bearbeiten': 45,
  'leistungen:bearbeiten': 45,
}

/** Floor eines Rechts (0 = keine Schranke). */
export function rightMinRank(right: Right): number {
  return RIGHT_MIN_RANK[right] ?? 0
}

/** Erreicht `rank` den Mindestrang des Rechts? */
export function rankAllowsRight(rank: number, right: Right): boolean {
  return rank >= rightMinRank(right)
}

const RIGHT_SET: ReadonlySet<string> = new Set(RIGHTS)

export function isRight(value: string): value is Right {
  return RIGHT_SET.has(value)
}

/** Filtert eine Rechte-Liste auf gültige, eindeutige Rechte (verwirft Unbekanntes/veraltete Tokens). */
export function cleanRights(rights: readonly string[] | null | undefined): Right[] {
  if (!Array.isArray(rights)) return []
  return [...new Set(rights.filter(isRight))] as Right[]
}

/** Liest die ROH vergebenen Rechte eines Nutzers robust: JSON-String (DB) oder Array (Domäne); defekt → []. */
export function userRights(user: UserLike | null | undefined): Right[] {
  if (!user) return []
  const raw = user.permissions
  if (Array.isArray(raw)) return cleanRights(raw)
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? cleanRights(parsed as string[]) : []
    } catch {
      return []
    }
  }
  return []
}

/**
 * EFFEKTIVE Rechte (M1): die roh vergebenen Rechte, gefiltert auf die, deren Floor der Rang erreicht.
 * Damit greift der Mindestrang KONTINUIERLICH — ein Positions-Downgrade streift zu mächtige Rechte
 * automatisch ab, ohne dass die roh gespeicherte Liste angefasst werden muss.
 */
export function effectiveRights(user: UserLike | null | undefined): Right[] {
  if (isSuperadmin(user)) return [...RIGHTS]
  const rank = userRank(user)
  return userRights(user).filter((r) => rankAllowsRight(rank, r))
}

/** Maßgebliche, rang-bewusste Rechteprüfung. JEDER Guard nutzt diese (nicht die rohe Liste). */
export function hasRight(user: UserLike | null | undefined, right: Right): boolean {
  if (isSuperadmin(user)) return true
  return rankAllowsRight(userRank(user), right) && userRights(user).includes(right)
}

/** Bereich lesbar? Bearbeiten schließt Lesen ein. */
export function canReadArea(user: UserLike | null | undefined, area: Area): boolean {
  return hasRight(user, `${area}:lesen` as Right) || hasRight(user, `${area}:bearbeiten` as Right)
}

/** Bereich bearbeitbar (Schreibrecht)? */
export function canWriteArea(user: UserLike | null | undefined, area: Area): boolean {
  return hasRight(user, `${area}:bearbeiten` as Right)
}

/** Belegungs-Plantafel lesbar? Bearbeiten schließt Lesen ein — Bereichs-Semantik, obwohl 'belegung' (noch)
 *  kein AREAS-Eintrag ist (siehe RIGHTS-Kommentar: Frontend-Matrix-Typisierung). */
export function canReadBelegung(user: UserLike | null | undefined): boolean {
  return hasRight(user, 'belegung:lesen') || hasRight(user, 'belegung:bearbeiten')
}

/** Belegungs-Plantafel bearbeitbar (Termin + Arbeitsplatz am Auftrag zuweisen)? */
export function canWriteBelegung(user: UserLike | null | undefined): boolean {
  return hasRight(user, 'belegung:bearbeiten')
}

/** Audit-Log (Nachweiskette) der eigenen Werkstatt lesbar? Kein Schreib-Pendant — audit_log ist append-only
 *  (DB-Trigger); 'audit' ist wie 'belegung' bewusst kein AREAS-Eintrag (siehe RIGHTS-Kommentar). */
export function canReadAudit(user: UserLike | null | undefined): boolean {
  return hasRight(user, 'audit:lesen')
}

/**
 * Rechte, die `actor` an einen Nutzer mit `targetRank` vergeben darf (R5/M4): Teilmenge der EFFEKTIVEN
 * Rechte des Actors, zusätzlich gefiltert auf die, deren Floor der ZIEL-Rang erreicht. So kann kein Recht
 * an einen zu niedrigen Rang „durchgereicht" werden — dieselbe Logik trägt auch den Cutover.
 */
export function grantableRights(actor: UserLike | null | undefined, targetRank: number): Right[] {
  const own = isSuperadmin(actor) ? [...RIGHTS] : effectiveRights(actor)
  return own.filter((r) => rankAllowsRight(targetRank, r))
}

/** Darf `actor` dem Ziel (Rang `targetRank`) genau dieses Recht geben/bestätigen? */
export function canGrantRight(actor: UserLike | null | undefined, targetRank: number, right: Right): boolean {
  return grantableRights(actor, targetRank).includes(right)
}

/**
 * Rollen-Presets V2 (Startvorschlag für NEUE Nutzer — Bestandsnutzer werden aus ihren EFFEKTIVEN
 * Alt-Rechten übernommen, siehe docs Abschnitt 2.7). Werkstattmeister ist bewusst BREIT (Entscheidung
 * 10.07.): Kunden/Leistungen/Planung/Produktivität wie heute — aber KEIN Umsatz/Team/Einstellungen.
 */
export const ROLE_PRESETS_V2: Record<string, Right[]> = {
  Chef: [...RIGHTS],
  Zweitchef: [...RIGHTS],
  Werkstattmeister: [
    'annahme:bearbeiten', 'werkstatt:bearbeiten', 'planung:bearbeiten', 'belegung:bearbeiten',
    'kunden:bearbeiten', 'leistungen:bearbeiten',
    'inventar:bearbeiten', 'einkauf:bearbeiten',
    'rechnungen:bearbeiten', 'produktivitaet:lesen',
    'arbeitszeit:lesen', 'alle_auftraege', 'auftrag_loeschen',
  ],
  Serviceannahme: [
    'annahme:bearbeiten', 'planung:bearbeiten', 'belegung:bearbeiten', 'kunden:bearbeiten',
    'rechnungen:bearbeiten', 'werkstatt:lesen', 'einkauf:lesen', 'alle_auftraege',
  ],
  Verkauf: ['annahme:bearbeiten', 'kunden:bearbeiten', 'rechnungen:bearbeiten', 'alle_auftraege'],
  Einkauf: ['inventar:bearbeiten', 'einkauf:bearbeiten', 'werkstatt:lesen'],
  Mechaniker: ['meine_auftraege', 'werkstatt:lesen'],
  Mitarbeiter: [],
}

/** Startrechte für eine Position (leer, wenn unbekannt). */
export function presetRightsForPosition(position: string | null | undefined): Right[] {
  return position != null && ROLE_PRESETS_V2[position] ? [...(ROLE_PRESETS_V2[position] as Right[])] : []
}

/** Ist der Nutzer Team-Verwalter? = effektiv `team:bearbeiten` (Ersatz der gedroppten role='chef'-Semantik). */
export function isTeamManager(user: UserLike | null | undefined): boolean {
  return canWriteArea(user, 'team')
}

/** Sieht der Nutzer ALLE Aufträge der Werkstatt (nicht nur die eigenen)? = das Recht `alle_auftraege`. */
export function seesAllOrders(user: UserLike | null | undefined): boolean {
  return hasRight(user, 'alle_auftraege')
}

/**
 * Grob-Rolle (nur Anzeige/Contract; jede echte Autorisierung läuft über Rechte/Position): aus den EFFEKTIVEN
 * Rechten abgeleitet. team-verwaltend → 'chef', sonst annahme-schreibend → 'annahme', sonst 'mechaniker'.
 */
export function coarseRole(user: UserLike | null | undefined): 'chef' | 'annahme' | 'mechaniker' {
  if (canWriteArea(user, 'team')) return 'chef'
  if (canWriteArea(user, 'annahme')) return 'annahme'
  return 'mechaniker'
}
